import { Vector2 } from '../vec';
import { OpenXmlPart } from './openXmlPart';
import { RelationshipPart } from './relationshipPart';
import type { RichText } from './richText';
import type { TablePart } from './tablePart';
import { type CellPosition, getCellPos, getCellRef } from './utils';
import type { XlsxFile } from './xlsxFile';

export type Cell = {
  ref: string;
  pos: CellPosition;
  value: string | RichText;
  type: string;
};

export type Row = {
  row: number;
  cells: Cell[];
  height: number | null;
};

export type Column = {
  min: number;
  max: number;
  width: number | null;
};

const DEFAULT_COLUMN_WIDTH = 8.43; // in `0` characters
const DEFAULT_ROW_HEIGHT = 15; // in points

export class SheetPart extends OpenXmlPart {
  public readonly rels: RelationshipPart | null;
  public readonly cells: Record<string, Cell> = {};
  public readonly rows: Record<number, Row> = {};
  public readonly columns: Column[] = [];
  public name: string | null = null;
  public readonly tables: TablePart[] = [];
  public defaultRowHeight: number | null = null;
  public defaultColWidth: number | null = null;

  constructor(filePath: string, originalXml: string, xlsxFile: XlsxFile) {
    super(filePath, originalXml, xlsxFile);

    this.rels = RelationshipPart.fromFile(filePath, xlsxFile);
    const sheetFormatPr = this.dom.getElementsByTagName('sheetFormatPr')[0];
    if (sheetFormatPr) {
      const defaultRowHeight = sheetFormatPr.getAttribute('defaultRowHeight');
      if (defaultRowHeight) {
        this.defaultRowHeight = Number(defaultRowHeight);
      }

      const defaultColWidth = sheetFormatPr.getAttribute('defaultColWidth');
      if (defaultColWidth) {
        this.defaultColWidth = Number(defaultColWidth);
      }
    }

    for (const colElement of this.dom.getElementsByTagName('col')) {
      this.columns.push(this.getColumnFromElement(colElement));
    }

    if (!this.rels) return;

    for (const element of this.dom.getElementsByTagName('tablePart')) {
      const tablePart = this.rels.getPartFromElement<TablePart>(element);
      if (tablePart) {
        this.tables.push(tablePart);
      }
    }
  }

  override init(): void {
    for (const rowElement of this.dom.getElementsByTagName('row')) {
      // this.xlsxFile.getSharedString being used is the reason this is not in
      // the constructor
      const row = this.getRowFromElement(rowElement);
      this.rows[row.row] = row;
    }
  }

  private getRowFromElement(element: Element): Row {
    const row: Row = {
      row: Number(element.getAttribute('r')!),
      cells: [],
      height: null,
    };

    for (const cellElement of element.getElementsByTagName('c')) {
      const cell = this.getCellFromElement(cellElement);
      if (cell) {
        row.cells.push(cell);
        this.cells[cell.ref] = cell;
      }
    }

    return row;
  }

  private getCellFromElement(element: Element): Cell | null {
    const ref = element.getAttribute('r')!;
    const ogValue = element.getElementsByTagName('v')[0]?.textContent ?? '';
    const type = element.getAttribute('t') ?? '';

    let value: string | RichText = ogValue;

    if (type === 's') {
      value = this.xlsxFile.getSharedString(Number(ogValue)) ?? ogValue;
    } else if (type === 'b') {
      value = ogValue === '1' ? 'TRUE' : 'FALSE';
    }

    return {
      ref,
      pos: getCellPos(ref),
      value,
      type,
    };
  }

  private getColumnFromElement(element: Element): Column {
    return {
      min: Number(element.getAttribute('min')!),
      max: Number(element.getAttribute('max')!),
      width: Number(element.getAttribute('width')),
    };
  }

  public getCell(col: number, row: number): Cell | null;
  public getCell(pos: CellPosition): Cell | null;
  public getCell(colOrPos: number | CellPosition, row?: number): Cell | null {
    if (typeof colOrPos === 'number') {
      return this.cells[getCellRef(colOrPos, row!)] ?? null;
    } else {
      return this.cells[getCellRef(colOrPos.col, colOrPos.row)] ?? null;
    }
  }

  /**
   * @param min The minimum viewport position in pixels
   * @param max The maximum viewport position in pixels
   * @returns An object containing the visible cells and the offset of the top-right visible cell
   */
  public getVisibleCells(
    min: Vector2,
    max: Vector2,
  ): {
    cells: Cell[];
    offset: Vector2;
  } {
    // TODO: calculate actual sizes
    const COL_SIZE = 8.43 * 10; // 1ch ~= 10px
    const ROW_SIZE = 20; // 15pt = 20px

    const colMin = Math.floor(min.x / COL_SIZE);
    const colMax = Math.ceil(max.x / COL_SIZE);
    const rowMin = Math.floor(min.y / ROW_SIZE);
    const rowMax = Math.ceil(max.y / ROW_SIZE);

    const cells: Cell[] = [];

    for (let col = colMin; col <= colMax; col++) {
      for (let row = rowMin; row <= rowMax; row++) {
        const cell = this.getCell(col, row);
        if (cell) {
          cells.push(cell);
        }
      }
    }

    return {
      cells,
      offset: new Vector2(
        Math.floor((min.x - colMin * COL_SIZE) / COL_SIZE),
        Math.floor((min.y - rowMin * ROW_SIZE) / ROW_SIZE),
      ),
    };
  }
}
